/** **Edit settings…** → "Link settings" (480px): the settings that act per link (U2), saved
 *  to the link at once with **Save link now** — never part of the tour draft.
 *
 *  - Subtitle: the address tail · its date or "any date" · created · registered.
 *  - Verification: two segmented controls, [Tour's setting · resolves to X] [Verify]
 *    [Don't verify] (null = follow the tour).
 *  - Staff registering through this link: the preferred and forced role (the operator's
 *    roles picker), with the model's help texts; a forced role carries a shield warning.
 *  - Departure choice (recurring tours; one muted line on fixed-date tours).
 *  - Save is disabled until something changed; a failure stays inline with Retry, a 404
 *    says the link was deleted elsewhere; Esc (or ✕) with changes asks before discarding. */
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import * as toursApi from '@/api/tours';
import type { NamedRef, TourDetail, TourSelfregLink, TourSelfregLinkPatch } from '@/api/tours';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { Segmented } from '@/components/Segmented';
import { useSnackbar } from '@/components/Snackbar';
import { ImmediateActionButton } from '@/features/records/actions/ImmediateActionButton';

import { addressTail, createdWords, registeredWords, scopeWords, tourVerifyResolves, verifyChoiceWords } from './linkWords';
import { errorCode, errorText, selfregLinksKey } from './queries';

export const DIALOG_WIDTH_PX = 480;

type Verify = 'tour' | 'on' | 'off';

const toVerify = (v: boolean | null): Verify => (v === null ? 'tour' : v ? 'on' : 'off');
const fromVerify = (v: Verify): boolean | null => (v === 'tour' ? null : v === 'on');

const RoleSchema = z.object({ id: z.number(), name: z.string().nullable() }).nullable();

const Schema = z.object({
  verify_email: z.enum(['tour', 'on', 'off']),
  verify_phone: z.enum(['tour', 'on', 'off']),
  preferred_role: RoleSchema,
  forced_role: RoleSchema,
  departure_overtime_minutes: z
    .string()
    .trim()
    .regex(/^\d+$/, 'Whole minutes, 0–1440')
    .refine((v) => Number(v) <= 1440, 'At most 1440 minutes (a day)'),
  same_day_next_departures: z.boolean(),
});

type FormValues = z.infer<typeof Schema>;

function valuesOf(link: TourSelfregLink): FormValues {
  return {
    verify_email: toVerify(link.verify_email),
    verify_phone: toVerify(link.verify_phone),
    preferred_role: link.preferred_role ? { id: link.preferred_role.id, name: link.preferred_role.name ?? null } : null,
    forced_role: link.forced_role ? { id: link.forced_role.id, name: link.forced_role.name ?? null } : null,
    departure_overtime_minutes: String(link.departure_overtime_minutes ?? 0),
    same_day_next_departures: Boolean(link.same_day_next_departures),
  };
}

/** The PATCH: only what changed. */
export function settingsPatch(before: FormValues, after: FormValues): TourSelfregLinkPatch {
  const patch: TourSelfregLinkPatch = {};
  if (after.verify_email !== before.verify_email) patch.verify_email = fromVerify(after.verify_email);
  if (after.verify_phone !== before.verify_phone) patch.verify_phone = fromVerify(after.verify_phone);
  if ((after.preferred_role?.id ?? null) !== (before.preferred_role?.id ?? null)) patch.preferred_role = after.preferred_role?.id ?? null;
  if ((after.forced_role?.id ?? null) !== (before.forced_role?.id ?? null)) patch.forced_role = after.forced_role?.id ?? null;
  if (Number(after.departure_overtime_minutes) !== Number(before.departure_overtime_minutes)) {
    patch.departure_overtime_minutes = Number(after.departure_overtime_minutes);
  }
  if (after.same_day_next_departures !== before.same_day_next_departures) {
    patch.same_day_next_departures = after.same_day_next_departures;
  }
  return patch;
}

/** A 400 `invalid`'s message per field (`ConsoleTourSelfregLinkError.errors`); {} for
 *  anything else. */
export function fieldErrorsOf(e: unknown): Record<string, string> {
  if (!(e instanceof ConsoleApiError)) return {};
  const errors: unknown = (e.body as Record<string, unknown> | undefined)?.errors;
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) return {};
  return Object.fromEntries(
    Object.entries(errors as Record<string, unknown>).filter((kv): kv is [string, string] => typeof kv[1] === 'string'),
  );
}

const SERVER_FIELDS: Record<string, keyof FormValues> = {
  verify_email: 'verify_email',
  verify_phone: 'verify_phone',
  preferred_role: 'preferred_role',
  forced_role: 'forced_role',
  departure_overtime_minutes: 'departure_overtime_minutes',
  same_day_next_departures: 'same_day_next_departures',
};

export function LinkSettingsDialog({
  detail,
  link,
  onClose,
}: {
  detail: Pick<TourDetail, 'id' | 'tenant' | 'is_bespoke' | 'selfreg_verify_email' | 'selfreg_verify_phone'>;
  /** The link being edited; null = closed. */
  link: TourSelfregLink | null;
  onClose: () => void;
}) {
  // One form per opening: its baseline is the link as it was when the dialog opened.
  return link ? <LinkSettingsForm key={link.id} detail={detail} link={link} onClose={onClose} /> : null;
}

function LinkSettingsForm({
  detail,
  link,
  onClose,
}: {
  detail: Pick<TourDetail, 'id' | 'tenant' | 'is_bespoke' | 'selfreg_verify_email' | 'selfreg_verify_phone'>;
  link: TourSelfregLink;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const subtitleId = useId();
  const [defaults] = useState(() => valuesOf(link));
  const [asking, setAsking] = useState(false);
  const {
    control,
    handleSubmit,
    setError,
    formState: { isDirty, errors },
    watch,
  } = useForm<FormValues>({ resolver: zodResolver(Schema), defaultValues: defaults, mode: 'onChange' });

  const save = useMutation({
    mutationFn: (patch: TourSelfregLinkPatch) => toursApi.updateSelfregLink(detail.id, link.id, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: selfregLinksKey(detail.id) });
      snackbar.show(`Settings of link ${addressTail(link)} saved.`, 'success');
      onClose();
    },
    onError: (e) => {
      if (errorCode(e) !== 'invalid') return;
      for (const [k, msg] of Object.entries(fieldErrorsOf(e))) {
        const field = SERVER_FIELDS[k];
        if (field) setError(field, { type: 'server', message: msg });
      }
    },
  });
  const submit = handleSubmit((values) => save.mutate(settingsPatch(defaults, values)));

  const gone = save.error instanceof ConsoleApiError && save.error.status === 404;
  const saveError = !save.isError
    ? null
    : gone
      ? 'This link was deleted elsewhere — nothing was saved.'
      : errorCode(save.error) === 'invalid'
        ? 'Some settings were refused — see the fields.'
        : errorText(save.error, 'Could not save the link’s settings.');

  /** Esc, ✕ and the backdrop: with changes, ask first. */
  const requestClose = () => {
    if (save.isPending) return;
    if (isDirty && !gone) setAsking(true);
    else onClose();
  };

  const forced = watch('forced_role');
  const verifyOptions = (which: 'email' | 'phone') => {
    const resolves = tourVerifyResolves(
      which === 'email' ? link.verify_email : link.verify_phone,
      which === 'email' ? link.effective_verify_email : link.effective_verify_phone,
      which === 'email' ? detail.selfreg_verify_email : detail.selfreg_verify_phone,
    );
    const resolvedWords = resolves === null ? 'the operator’s setting' : verifyChoiceWords(resolves);
    return [
      {
        value: 'tour' as const,
        label: verifyChoiceWords(null),
        detail: `resolves to ${resolvedWords}`,
        name: `Tour's setting, resolves to ${resolvedWords}`,
      },
      { value: 'on' as const, label: verifyChoiceWords(true) },
      { value: 'off' as const, label: verifyChoiceWords(false) },
    ];
  };
  const pickRoles = async (q: string): Promise<NamedRef[]> => {
    if (!detail.tenant) return [];
    const r = await toursApi.picker(detail.tenant.id, 'roles', q.trim() || undefined);
    return r.results.map((o) => ({ id: o.id, name: o.name ?? `#${o.id}` }));
  };

  return (
    <Dialog
      open
      onClose={requestClose}
      maxWidth={false}
      PaperProps={{ sx: { width: DIALOG_WIDTH_PX, maxWidth: 'calc(100% - 32px)' }, 'data-selfreg-settings': '' } as object}
      aria-labelledby="selfreg-settings-title"
      aria-describedby={subtitleId}
    >
    <Box
      component="form"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      {/* Not DialogTitle: it takes the dialog's aria-labelledby id for itself, which would make
          the subtitle part of the dialog's name. */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', pl: 3, pr: 1, pt: 2, pb: 0.5 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography id="selfreg-settings-title" variant="h6" component="h2">
            Link settings
          </Typography>
          <Typography id={subtitleId} variant="body2" color="text.secondary" sx={{ fontFamily: 'inherit' }} data-settings-subtitle>
            <Box component="span" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: '0.8125rem' }}>
              {addressTail(link)}
            </Box>
            {` · ${scopeWords(link)} · created ${createdWords(link.created_ts).text} · ${registeredWords(link.registered_count).toLowerCase()}`}
          </Typography>
        </Box>
        <IconButton aria-label="Close" size="small" onClick={requestClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <BoltOutlinedIcon aria-hidden sx={{ fontSize: 14 }} />
          Saved to the link at once, not part of the tour draft.
        </Typography>

        <Box component="fieldset" sx={{ border: 0, p: 0, m: 0, display: 'grid', gap: 1 }}>
          <Typography component="legend" variant="subtitle2" sx={{ mb: 0.5 }}>
            Verification
          </Typography>
          {(['email', 'phone'] as const).map((which) => (
            <Box key={which} sx={{ display: 'grid', gap: 0.5 }}>
              <Typography id={`selfreg-verify-${which}`} variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {which === 'email' ? 'Verify email' : 'Verify phone'}
              </Typography>
              <Controller
                control={control}
                name={which === 'email' ? 'verify_email' : 'verify_phone'}
                render={({ field }) => (
                  <Segmented<Verify>
                    labelledBy={`selfreg-verify-${which}`}
                    value={field.value}
                    onChange={field.onChange}
                    options={verifyOptions(which)}
                    disabled={save.isPending}
                    dataAttr={`verify-${which}`}
                  />
                )}
              />
            </Box>
          ))}
        </Box>

        <Box component="fieldset" sx={{ border: 0, p: 0, m: 0, display: 'grid', gap: 1.5 }}>
          <Typography component="legend" variant="subtitle2" sx={{ mb: 0.5 }}>
            Staff registering through this link
          </Typography>
          <Controller
            control={control}
            name="preferred_role"
            render={({ field }) => (
              <RemoteAutocomplete<NamedRef>
                label="Preferred role"
                size="small"
                fetchOnEmpty
                queryFn={pickRoles}
                getOptionLabel={(o) => o.name ?? `#${o.id}`}
                getOptionKey={(o) => o.id}
                value={field.value}
                onChange={(v) => field.onChange((v as NamedRef | null) ?? null)}
                placeholder="None"
                errorText="Couldn’t load the operator’s roles"
                helperText={errors.preferred_role?.message ?? 'Assigned to staff registering through this link only if they already have the role.'}
                error={Boolean(errors.preferred_role)}
                disabled={save.isPending || !detail.tenant}
              />
            )}
          />
          <Box sx={{ display: 'grid', gap: 0.5 }}>
            <Controller
              control={control}
              name="forced_role"
              render={({ field }) => (
                <RemoteAutocomplete<NamedRef>
                  label="Forced role"
                  size="small"
                  fetchOnEmpty
                  queryFn={pickRoles}
                  getOptionLabel={(o) => o.name ?? `#${o.id}`}
                  getOptionKey={(o) => o.id}
                  value={field.value}
                  onChange={(v) => field.onChange((v as NamedRef | null) ?? null)}
                  placeholder="None"
                  errorText="Couldn’t load the operator’s roles"
                  helperText={errors.forced_role?.message ?? 'Assigned to staff registering through this link regardless of their roles.'}
                  error={Boolean(errors.forced_role)}
                  disabled={save.isPending || !detail.tenant}
                />
              )}
            />
            {forced && (
              <Typography variant="caption" sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }} data-forced-warning>
                <GppMaybeOutlinedIcon aria-hidden sx={{ fontSize: 16, color: 'warning.dark', mt: '1px' }} />
                Anyone who registers as staff through this link gets the role “{forced.name}”, whatever roles they
                have — keep the link to people who should have it.
              </Typography>
            )}
          </Box>
        </Box>

        <Box component="fieldset" sx={{ border: 0, p: 0, m: 0, display: 'grid', gap: 1 }}>
          <Typography component="legend" variant="subtitle2" sx={{ mb: 0.5 }}>
            Departure choice
          </Typography>
          {detail.is_bespoke ? (
            <Typography variant="body2" color="text.secondary" data-departure-note>
              Fixed-date tour: guests register for its one date, so there is no departure to choose.
            </Typography>
          ) : (
            <>
              <Controller
                control={control}
                name="departure_overtime_minutes"
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    label="Departure overtime (minutes)"
                    inputMode="numeric"
                    error={Boolean(errors.departure_overtime_minutes)}
                    helperText={
                      errors.departure_overtime_minutes?.message ??
                      'How long after a departure starts it is still offered by default (0–1440).'
                    }
                    disabled={save.isPending}
                  />
                )}
              />
              <Controller
                control={control}
                name="same_day_next_departures"
                render={({ field }) => (
                  <Box sx={{ display: 'grid' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          disabled={save.isPending}
                        />
                      }
                      label="Only today’s departures"
                    />
                    <Typography variant="caption" color="text.secondary">
                      Offer only today’s departures (in the tour’s time zone).
                    </Typography>
                  </Box>
                )}
              />
            </>
          )}
        </Box>

        {saveError && (
          <Typography variant="body2" color="error" role="alert" sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }} data-settings-error>
            <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16 }} />
            {saveError}
            {!gone && errorCode(save.error) !== 'invalid' && (
              <Button size="small" color="inherit" type="submit" sx={{ minWidth: 0, py: 0, ml: 'auto' }}>
                Retry
              </Button>
            )}
          </Typography>
        )}
        {asking && (
          <Box
            role="alertdialog"
            aria-label="Discard your changes?"
            sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}
            data-settings-discard
          >
            <Typography variant="body2" sx={{ flex: 1 }}>
              Discard your changes to this link?
            </Typography>
            <Button size="small" autoFocus onClick={() => setAsking(false)}>
              Keep editing
            </Button>
            <Button size="small" color="error" onClick={onClose}>
              Discard
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={requestClose} disabled={save.isPending}>
          {gone ? 'Close' : 'Cancel'}
        </Button>
        <ImmediateActionButton variant="contained" type="submit" disabled={!isDirty || save.isPending || gone}>
          {save.isPending ? 'Saving…' : 'Save link now'}
        </ImmediateActionButton>
      </DialogActions>
    </Box>
    </Dialog>
  );
}
